/**
 * The thread: the focused path through a graph, as a list a column can render.
 *
 * A reasoning graph is a tree plus bridges, and a reading column is a line —
 * so the column shows ONE path, seed to anchor, and names the branches it is
 * not taking as forks a reader can pivot to. The anchor is the selection, and
 * falls back to the seed when nothing is selected: an open exploration always
 * has a readable thread, where the old panel had an empty note.
 *
 * Pure and a LEAF, like `cues.ts`: it imports `./types` and nothing else, so
 * the store can import it without joining a cycle. The lineage walk is absorbed
 * here rather than imported from the store for that reason — the store's own
 * `lineage` sits in a module this one must not depend on.
 *
 * Folding is deliberately not this module's business. Which branches are folded
 * is view state the canvas frame owns (`useGraphFrame`), and a thread that
 * dropped folded forks would disagree with the canvas about what exists; the
 * UI decorates fork chips from the same source the canvas reads.
 */

import type { ReasonNode, Session } from './types'

export interface ThreadCard {
  node: ReasonNode
  /** Children the path does not take — the branches a reader can pivot to,
   *  in `session.order` (arrival order). On the anchor itself: every child. */
  forks: ReasonNode[]
}

export interface Thread {
  /** What the reading is anchored on: the selection, else the seed. Null only
   *  when the session has no nodes at all. */
  anchorId: string | null
  /** Seed first, anchor last. Empty exactly when `anchorId` is null. */
  cards: ThreadCard[]
}

/** Root → anchor, with a seen-set so a malformed parent cycle terminates
 *  instead of hanging the render — the same guard the store's `lineage` keeps. */
function pathTo(s: Session, id: string): ReasonNode[] {
  const out: ReasonNode[] = []
  const seen = new Set<string>()
  let cur: string | null = id
  while (cur && !seen.has(cur)) {
    seen.add(cur)
    const n: ReasonNode | undefined = s.nodes[cur]
    if (!n) break
    out.unshift(n)
    cur = n.parentId
  }
  return out
}

export function threadFor(s: Session, selectedId: string | null): Thread {
  // The anchor: a live selection, else the seed. A selection whose node has
  // been pruned since falls back rather than throwing — the thread is read
  // during the very renders in which a prune or an undo is settling.
  const root = s.order.find((id) => s.nodes[id]?.parentId === null) ?? null
  const anchorId = selectedId && s.nodes[selectedId] ? selectedId : root
  if (!anchorId) return { anchorId: null, cards: [] }

  // Children in arrival order — one pass over `order`, which already IS that
  // order. A bridge-realised node has one `parentId` like any other, so it
  // appears under one parent and once.
  const kids = new Map<string, ReasonNode[]>()
  for (const id of s.order) {
    const n = s.nodes[id]
    if (!n || n.parentId === null) continue
    const list = kids.get(n.parentId)
    if (list) list.push(n)
    else kids.set(n.parentId, [n])
  }

  const path = pathTo(s, anchorId)
  const cards = path.map((node, i) => {
    const next = path[i + 1]?.id
    const forks = (kids.get(node.id) ?? []).filter((c) => c.id !== next)
    return { node, forks }
  })
  return { anchorId, cards }
}
